import { Component, type ReactNode } from 'react';
import { logger } from '../../utils/logger';
import { EBTN_SECONDARY } from './editorButtonStyles';

declare const __APP_VERSION__: string;

/** The running build, short form, as the editorDebug overlay and the boot message show it. */
export const BUILD_ID = (typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown').slice(0, 8);

/**
 * Keeps a render error in the lesson editor from blanking the page.
 *
 * `variant="editor"`: replaces the editor with a short message carrying the
 * error text and the build id, and a reload. `variant="overlay"`: the
 * editorDebug overlay fails on its own, as one line, and never takes the editor
 * down with it.
 */
export class EditorErrorBoundary extends Component<{ variant: 'editor' | 'overlay'; children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error(`Lesson editor ${this.props.variant} crashed`, { error: error.message, build: BUILD_ID, componentStack: info.componentStack }, error);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const text = error.message || String(error);
    if (this.props.variant === 'overlay') {
      return (
        <div className="fixed left-1 top-1 rounded-md p-2 font-mono text-[10px] leading-[13px] text-white pointer-events-none" style={{ zIndex: 2147483647, background: 'rgba(0,0,0,0.78)' }}>
          editorDebug overlay error: {text} (build {BUILD_ID})
        </div>
      );
    }
    return (
      <div role="alert" className="fixed inset-0 z-[100] flex items-center justify-center px-4" style={{ background: 'var(--editor-bg, #0b1220)' }}>
        <div className="max-w-sm w-full flex flex-col items-center gap-3 text-center">
          {/* Body 17/22, Footnote 13/18. */}
          <p className="text-[17px] leading-[22px] font-semibold text-white">The editor hit an error.</p>
          <p className="text-[13px] leading-[18px] font-mono text-white/80 break-words w-full">{text}</p>
          <p className="text-[13px] leading-[18px] text-white/50">Build {BUILD_ID}</p>
          <button type="button" className={EBTN_SECONDARY} onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
